import { useState } from 'react';
import { Card, Select, Switch, Button, Table, Loader, Center, Text, Modal, Stack, TextInput, Textarea, Group, Badge, SegmentedControl, Tooltip } from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconAlertTriangle, IconBook} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { useClasses, useSubjects } from '../hooks/useLookups';
import { useAuth } from '../contexts/AuthContext';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { useDebouncedValue } from '@mantine/hooks';
import { PageHeader } from '../components/PageHeader';
import { CreatableSelect, QuickSubjectForm } from '../components/QuickCreate';
import { SectionCard } from '../components/SectionCard';
import { formatDate } from '../lib/format';

interface Homework {
  id: string; title: string; description?: string; dueDate?: string;
  subject?: { name: string }; teacher?: { id: string; fullName: string } | null;
  /**
   * T6: "muddati o'tgan" — SERVER hisoblaydi (Toshkent kalendari bo'yicha kun
   * aniqligida). Mijozda `new Date(dueDate) < new Date()` bilan hisoblansa,
   * boshqa mintaqadagi/soati adashgan qurilmada bugungi vazifa ham qizil chiqardi.
   */
  isOverdue?: boolean;
}
/** GET /homework/teachers — FAQAT vazifa bergan foydalanuvchilar */
interface HomeworkTeacherOpt { id: string; fullName: string; homeworkCount: number }

/**
 * Lokal sana → "YYYY-MM-DD" (backend uni TOSHKENT kuni deb o'qiydi).
 * `toISOString()` EMAS — UTC+5 da kun bir kun orqaga siljirdi (Grades.tsx dagi qoida).
 */
const iso = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

export default function HomeworkPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classId, setClassId] = useState<string | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const { data: classes } = useClasses();
  const { data: subjects } = useSubjects();
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [subjF, setSubjF] = useState('');
  // "Faqat menikilar" — backend ?mine=1 bilan teacherId'ni tokendan oladi.
  // Faqat dars beruvchi rollar uchun ma'noli (staff barcha vazifalarni ko'radi).
  const isTeacher = user?.role === 'TEACHER' || user?.role === 'HOMEROOM_TEACHER';
  const [mineOnly, setMineOnly] = useState(false);
  // T6: "kim bergani" filtri. `mine=1` bilan birga yuborilsa backend `mine` ni USTUN
  // ko'radi — chalkashmaslik uchun "faqat meniki" yoqilganda select bloklanadi.
  const [teacherF, setTeacherF] = useState('');
  // T6: muddat oralig'i (Toshkent kunlari). Berilganda MUDDATSIZ vazifalar ro'yxatga tushmaydi.
  const [dueRange, setDueRange] = useState<[Date | null, Date | null]>([null, null]);
  // '' — hammasi, 'true' — muddati o'tganlar, 'false' — faol (muddatsizlar ham shu yerda)
  const [overdueF, setOverdueF] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const mineActive = isTeacher && mineOnly;
  const { data: hwTeachers } = useQuery({
    queryKey: ['homework-teachers'],
    queryFn: async () => (await api.get<HomeworkTeacherOpt[]>('/homework/teachers')).data,
  });
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    classId: classId || undefined, q: qDeb || undefined, subjectId: subjF || undefined,
    mine: mineActive ? '1' : undefined,
    teacherId: !mineActive && teacherF ? teacherF : undefined,
    dueFrom: iso(dueRange[0]), dueTo: iso(dueRange[1]),
    overdue: overdueF || undefined,
    sort,
  };
  const { items, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Homework>('homework', '/homework', filtrlar);
  const anyFilter = !!(qDeb || subjF || mineActive || teacherF || dueRange[0] || dueRange[1] || overdueF);

  const form = useForm({
    initialValues: { subjectId: '', title: '', description: '', dueDate: null as Date | null, fileUrl: '' },
    validate: {
      subjectId: (v) => (!v ? t('homeworkPage.subjectRequired') : null),
      title: (v) => (!v ? t('homeworkPage.titleRequired') : null),
    },
  });

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/homework', { ...v, classId, dueDate: v.dueDate?.toISOString() }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('homeworkPage.created') }); qc.invalidateQueries({ queryKey: ['homework'] }); form.reset(); close(); },
  });

  return (
    <>
      <PageHeader
        title={t('pages.homework.title')}
        action={<Button leftSection={<IconPlus size={18} />} disabled={!classId} onClick={open}>{t('homeworkPage.add')}</Button>}
      />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('homeworkPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'createdAt:desc', label: t('homeworkPage.sortNew') },
          { value: 'dueDate:asc', label: t('homeworkPage.sortDueSoon') },
          { value: 'title:asc', label: t('homeworkPage.sortTitleAsc') },
        ]}
      >
        <Select
          placeholder={t('fields.subject')}
          data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
          value={subjF || null}
          onChange={(v) => { setSubjF(v ?? ''); setPage(1); }}
          clearable searchable w={160}
        />
        <Select
          placeholder={t('homeworkPage.teacher')}
          // Yorliqdagi son — o'sha o'qituvchi bergan vazifalar soni
          data={(hwTeachers ?? []).map((tt) => ({ value: tt.id, label: `${tt.fullName} (${tt.homeworkCount})` }))}
          value={teacherF || null}
          onChange={(v) => { setTeacherF(v ?? ''); setPage(1); }}
          disabled={mineActive}
          description={mineActive ? t('homeworkPage.teacherDisabledHint') : undefined}
          clearable searchable w={190}
          nothingFoundMessage={t('common.noResults')}
        />
        <DatePickerInput
          type="range"
          placeholder={t('homeworkPage.dueRange')}
          value={dueRange}
          onChange={(v) => { setDueRange(v); setPage(1); }}
          valueFormat="DD.MM.YYYY"
          description={dueRange[0] || dueRange[1] ? t('homeworkPage.dueRangeHint') : undefined}
          clearable w={210}
        />
        <SegmentedControl
          size="sm"
          value={overdueF}
          onChange={(v) => { setOverdueF(v); setPage(1); }}
          data={[
            { value: '', label: t('common.all') },
            { value: 'true', label: t('homeworkPage.overdueOnly') },
            { value: 'false', label: t('homeworkPage.activeOnly') },
          ]}
        />
        {isTeacher && (
          <Switch
            mb={8}
            label={t('homeworkPage.mineOnly')}
            checked={mineOnly}
            onChange={(e) => { setMineOnly(e.currentTarget.checked); setPage(1); }}
          />
        )}
        <TableExport<Homework>
          endpoint="/homework"
          params={filtrlar}
          filename={t('nav.homework')}
          ustunlar={[
            { key: 'subject', label: t('homeworkPage.subject'), get: (r) => r.subject?.name ?? '' },
            { key: 'title', label: t('homeworkPage.task') },
            { key: 'dueDate', label: t('homeworkPage.due') },
          ]}
          sanaMaydoni={(r) => r.dueDate}
        />
      </TableToolbar>
      <Card mb="md">
        <Select label={t('homeworkPage.class')} placeholder={t('common.selectPlaceholder')} data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))} value={classId} onChange={setClassId} searchable w={220} />
      </Card>

      {/* Karta doim ko'rinadi — ichida tanlov/yuklanish/bo'sh holat/jadval almashadi */}
      <SectionCard title={t('common.list')} icon={IconBook} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {!classId ? (
          <Center h={140}><Text c="dimmed">{t('homeworkPage.selectClass')}</Text></Center>
        ) : isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !items.length ? (
          <Center h={140}><Text c="dimmed">{anyFilter ? t('common.noMatch') : t('homeworkPage.empty')}</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('homeworkPage.subject')}</Table.Th>
                  <Table.Th>{t('homeworkPage.task')}</Table.Th>
                  {/* T6: "kim bergani" — avval ustun oxirida va faqat "O'qituvchi" deb turardi */}
                  <Table.Th>{t('homeworkPage.teacher')}</Table.Th>
                  <Table.Th>{t('homeworkPage.due')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((h) => (
                  <Table.Tr
                    key={h.id}
                    style={{ cursor: 'pointer' }}
                    // Muddati o'tgan qator butunlay ajratiladi (faqat nishon emas).
                    // `--mantine-color-red-light` mavzuga moslashadi — qorong'i rejimda ham o'qiladi.
                    bg={h.isOverdue ? 'var(--mantine-color-red-light)' : undefined}
                    onClick={() => navigate(`/homework/${h.id}`)}
                  >
                    <Table.Td fw={500}>{h.subject?.name}</Table.Td>
                    <Table.Td>{h.title}</Table.Td>
                    <Table.Td>{h.teacher?.fullName ?? '—'}</Table.Td>
                    <Table.Td>
                      {h.dueDate ? (
                        <Tooltip label={h.isOverdue ? t('homeworkPage.overdueHint') : t('homeworkPage.due')} withArrow>
                          <Badge
                            color={h.isOverdue ? 'red' : 'orange'}
                            variant="light"
                            leftSection={h.isOverdue ? <IconAlertTriangle size={12} /> : undefined}
                          >
                            {formatDate(h.dueDate)}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">{t('homeworkPage.noDue')}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={close} title={t('homeworkPage.modalTitle')} centered>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <CreatableSelect
              label={t('homeworkPage.subject')}
              required
              data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={form.values.subjectId}
              onChange={(v) => form.setFieldValue('subjectId', v || '')}
              error={form.errors.subjectId}
              createTitle={t('homeworkPage.newSubject')}
              renderCreate={(onCreated) => <QuickSubjectForm onCreated={onCreated} />}
            />
            <TextInput label={t('homeworkPage.titleField')} required {...form.getInputProps('title')} />
            <Textarea label={t('homeworkPage.description')} autosize minRows={3} {...form.getInputProps('description')} />
            <DateInput label={t('homeworkPage.dueDate')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('dueDate')} />
            <TextInput label={t('homeworkPage.fileUrl')} placeholder="https://..." {...form.getInputProps('fileUrl')} />
            <Group justify="flex-end"><Button variant="default" onClick={close}>{t('common.cancel')}</Button><Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
